import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../context/WorkspaceContext';
import { supabase } from '../../lib/supabase';
import SignUpLayout from './SignUpLayout';
import AvailablePlansDisplay from '../../components/PaymentManager/AvailablePlansDisplay';
import Button from '../../components/Button/Button';
import { TierPlan } from '../../components/PaymentManager/PaymentManager';
import BillingSwitcher from '../../components/PaymentManager/BillingSwitcher';

const ChoosePlan = () => {
  const { selectedTeam } = useWorkspace();
  const navigate = useNavigate();

  const [tierPlans, setTierPlans] = useState<TierPlan[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [startFade, setStartFade] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingProducts(true);
      setProductsError(null);
      try {
        const { data: productsData, error: functionError } = await supabase.functions.invoke(
          'fetch-polar-products',
          { method: 'GET' }
        );
        if (functionError) throw new Error(functionError.message);
        if (!productsData || !Array.isArray(productsData.items)) {
          throw new Error('Invalid product data');
        }
        const groupedByTier: { [key: string]: any[] } = {};
        productsData.items.forEach((item: any) => {
          const tier = item.metadata?.tier?.toLowerCase();
          if (tier) {
            if (!groupedByTier[tier]) groupedByTier[tier] = [];
            groupedByTier[tier].push(item);
          }
        });
        const tierOrder = ['team', 'enterprise', 'free'];
        const tierPlansData: TierPlan[] = [];
        tierOrder.forEach((tierName: string) => {
          const group = groupedByTier[tierName];
          if (group) {
            const monthlyItem = group.find((item: any) => item.prices?.[0]?.recurring_interval === 'month');
            const yearlyItem = group.find((item: any) => item.prices?.[0]?.recurring_interval === 'year');
            const monthlyPrice = monthlyItem?.prices?.[0];
            const yearlyPrice = yearlyItem?.prices?.[0];
            const description = monthlyItem?.description ?? yearlyItem?.description;
            tierPlansData.push({
              tierName: tierName.charAt(0).toUpperCase() + tierName.slice(1),
              description,
              monthlyPriceId: monthlyPrice?.id,
              monthlyDisplayPrice: formatPrice(monthlyPrice),
              monthlyAmount: monthlyPrice?.price_amount,
              monthlyProductId: monthlyItem?.id,
              yearlyPriceId: yearlyPrice?.id,
              yearlyDisplayPrice: formatPrice(yearlyPrice),
              yearlyAmount: yearlyPrice?.price_amount,
              yearlyProductId: yearlyItem?.id,
            });
          }
        });
        setTierPlans(tierPlansData);
      } catch (err: any) {
        setProductsError(err.message || 'Failed to load plans');
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    if (!loadingProducts) {
      const timer = setTimeout(() => setStartFade(true), 50);
      return () => clearTimeout(timer);
    } else {
      setStartFade(false);
    }
  }, [loadingProducts]);

  function formatPrice(price: any) {
    if (!price) return undefined;
    if (price.amount_type === 'free') return 'Free';
    if (price.price_amount === undefined || !price.price_currency || !price.recurring_interval) return undefined;
    const amount = price.price_amount / 100;
    const currencySymbol = price.price_currency.toUpperCase() === 'USD' ? '$' : price.price_currency;
    const interval = price.recurring_interval;
    return `${currencySymbol}${amount.toFixed(2)} / ${interval}`;
  }

  const handlePurchase = async (priceId?: string, productId?: string) => {
    if (!priceId || !productId || !selectedTeam?.id) {
      setCheckoutError('Cannot initiate purchase. Missing required information.');
      return;
    }
    setIsCreatingCheckout(priceId);
    setCheckoutError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-polar-checkout',
        {
          method: 'POST',
          body: { priceId, productId, teamId: selectedTeam.id },
        }
      );
      if (error) throw new Error(error.message);
      if (!data?.checkoutUrl) throw new Error('Checkout URL not received.');
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setCheckoutError(err.message || 'Failed to start checkout process.');
    } finally {
      setIsCreatingCheckout(null);
    }
  };

  const handleSkip = () => {
    navigate('/overview');
  };

  return (
    <SignUpLayout
      title="Choose your plan"
      subtitle="You can try for free, or pick a paid plan now."
      currentStep={4}
      totalSteps={4}
      wide
    >
      {loadingProducts ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px' }}>
          <Button variant="text" size="large" isLoading={true} disabled={true} />
        </div>
      ) : (
        <>
          <div className="onboarding-plans-container">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <BillingSwitcher
                billingInterval={billingInterval}
                onBillingIntervalChange={setBillingInterval}
              />
            </div>
            <AvailablePlansDisplay
              tierPlans={tierPlans}
              billingInterval={billingInterval}
              onBillingIntervalChange={setBillingInterval}
              onPurchase={handlePurchase}
              isCreatingCheckout={isCreatingCheckout}
              checkoutError={checkoutError}
              productsError={productsError}
              startFade={startFade}
              hideHeader
              hideFreeDebug={true}
            />
          </div>
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Button variant="text" size="medium" onClick={handleSkip}>
              Skip for now
            </Button>
          </div>
        </>
      )}
    </SignUpLayout>
  );
};

export default ChoosePlan; 